import { SecureStorage } from '@aparajita/capacitor-secure-storage'

import { isNative } from '@/shared/api/platform'

const KEY = 'mp_refresh_token'

/**
 * Guarda el refresh token en almacenamiento seguro del dispositivo (Keychain en
 * iOS, EncryptedSharedPreferences en Android) SOLO en nativo. En web es no-op: la
 * web usa la cookie httpOnly y el token nunca toca JS.
 *
 * Motivo: la cookie de refresh es `SameSite=Strict`, same-site en web pero
 * cross-site desde el origen de la WebView (`capacitor://localhost`), así que el
 * navegador embebido no la envía. Ver docs/specs/2026-09-03-auth-refresh-nativo.md.
 */
export const refreshTokenStore = {
  async get(): Promise<string | null> {
    if (!isNative()) return null
    try {
      return await SecureStorage.getItem(KEY)
    } catch {
      return null
    }
  },
  async set(token: string): Promise<void> {
    if (!isNative()) return
    try {
      await SecureStorage.setItem(KEY, token)
    } catch {
      // Almacenamiento no disponible: la sesión no persistirá entre arranques,
      // pero no rompemos el login en curso.
    }
  },
  async clear(): Promise<void> {
    if (!isNative()) return
    try {
      await SecureStorage.removeItem(KEY)
    } catch {
      // noop
    }
  },
}

/** Body de `/auth/refresh` y `/auth/logout`: token guardado (nativo) o `undefined` (web). */
export async function nativeRefreshBody(): Promise<{ refreshToken: string } | undefined> {
  if (!isNative()) return undefined
  const token = await refreshTokenStore.get()
  return token ? { refreshToken: token } : undefined
}

/** Persiste el refresh rotado que devuelve el backend en el body (solo nativo). */
export async function persistRotatedRefresh(refreshToken?: string | null): Promise<void> {
  if (isNative() && refreshToken) {
    await refreshTokenStore.set(refreshToken)
  }
}
